import { routing } from "@/i18n/routing"
import { LOCALE_PREFIX_RE, PUBLIC_ROUTES } from "@/utils/constants"
import { SelfType } from "@/interfaces/self_types";
import { GameType } from "@/interfaces/games_types";
import { ProfileType } from "@/interfaces/profile_types";

export function getLocaleFromPath(pathname: string) {
  // /es/xxx -> es
  // /es-ES/xxx -> es-ES
  const match = pathname.match(LOCALE_PREFIX_RE)
  // return match?.[1] ?? routing.defaultLocale
  return match?.[1]
}

export function stripLocale(pathname: string) {
  // /es/xxx -> /xxx
  // /es-ES/xxx -> /xxx
  return pathname.replace(LOCALE_PREFIX_RE, '') || '/'
}

export function isPublicRoute(pathNoLocale: string) {
  const normalizedPath = pathNoLocale.toLowerCase()

  return PUBLIC_ROUTES.some((p) => {
    const normalizedPublic = p.toLowerCase()
    return normalizedPath === normalizedPublic || normalizedPath.startsWith(normalizedPublic + '/')
  })
}

export function userHasActivePlan (user: SelfType) {
  return user.product !== undefined && user.product !== null &&
          user.purchase !== undefined && user.purchase !== null &&
          user.product.id !== '' &&
          (user.purchase.subs_status === 3 ||
          (new Date(user.purchase.expiration_time) > new Date() &&
          (user.purchase.subs_status === 1 ||
            user.purchase.subs_status === 2 ||
            user.purchase.subs_status === 4 ||
            user.purchase.subs_status === 5 ||
            user.purchase.subs_status === 7))
          )
}

export function intToBitArray (number:number) {
  return (number >>> 0).toString(2).padStart(8, '0')
}

export function gameHasWindows (game:GameType) {
  const platforms = intToBitArray(game.platform)
  return platforms[7] === '1'
}

export function gameHasHTML5 (game:GameType) {
  const platforms = intToBitArray(game.platform)
  return platforms[4] === '1'
}

/**
 * Check whether the IP is geolocatable, meaning whether it is public
 * @param ip client IP
 * @returns Indicate whether it is public or not
 */
export function isPublicIP (ip: string): boolean {
  // IPv4 privada
  if (/^(10\.|192\.168\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(ip)) return false;
  // IPv6 local
  if (/^fc|^fd|^fe80|^::1/.test(ip)) return false;
  // Localhost IPv4
  if (ip === '127.0.0.1') return false;
  // Vacía o indefinida
  if (!ip) return false;
  return true;
}

/**
 * Gets the HTML5 configuration parameters from the exec_url returned by the API when launching a game,
 * and combines them with the profile and game information to create the final set of parameters needed
 * to launch the HTML5 game
 * 
 * @param execUrlParams Parameters string extracted from response returned by the API
 * @param profile Profile of the user launching the game
 * @param game Information of the game being launched
 * @returns Parameters to be included in the URL when launching the HTML5 game
 */
export function getHTML5ConfigParams(execUrlParams: string, profile: ProfileType, game: GameType) {
  // Remove the prefix and parse the parameters
  let paramString = execUrlParams.replace(/^ludiumlab:\/\//, '')
  const paramPairs = paramString.split('&')
  const execParams: Record<string, string> = {}
  paramPairs.forEach(pair => {
    const [key, ...rest] = pair.split('=')
    execParams[key] = rest.join('=')
  })

  const urlParams: Record<string, string | number | boolean> = {
    server: execParams.server,
    token: execParams.token ? encodeURIComponent(execParams.token.replaceAll(' ', '+')) : '',
    lang: execParams.lang,
    game: execParams.game,
    gameId: execParams.game_id,
    sessionType: execParams.session_type,
    profileId: execParams.profile_id,
    roomCode: execParams.room_code,
    containerID: 'sorastream-get-start',
    profileName: profile.name || '',
    profileImage: profile.avatar?.image_path || '',
    inactivityWarningTime: parseInt(process.env.NEXT_PUBLIC_INACTIVITY_WARNING_TIME!) || 480000,
    inactivityExitGameTime: parseInt(process.env.NEXT_PUBLIC_INACTIVITY_EXIT_GAME_TIME!) || 600000,
    urlAPI: process.env.NEXT_PUBLIC_BACKEND_ENDPOINT || '',
    profileType: profile.role || '',
    keyboardGuide: !!process.env.NEXT_PUBLIC_FEATURE_KEYBOARD_GUIDE || false,
    keyboardGuideResponsive: !!process.env.NEXT_PUBLIC_FEATURE_KEYBOARD_GUIDE_RESPONSIVE || false,
    steamLocationInfoMsg: !!process.env.NEXT_PUBLIC_FEATURE_STEAM_LOCATION_INFO_MSG || false,
    timeControlSettingsEndpoint: profile ? `${process.env.NEXT_PUBLIC_BACKEND_ENDPOINT}profiles/${profile.id}/time-remaining` : '',
    controlsSupported: game.supported_controls,
    backgroundImage: game.thumb_horiz_large.esc_400_url,
    backgroundImageVertical: game.thumb_vertical.esc_400_url
  }

  const result = new URLSearchParams([
    ['server', String(urlParams.server)],
    ...Object.entries(urlParams)
      .filter(([key]) => key !== 'server')
      .map(([key, value]) => [key, String(value)])
  ]).toString()
  console.log(result)
  return result
}